import React, { useState } from "react";
import {
	Alert,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	MenuItem,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { useEinvoicing } from "services/hooks/einvoicing";

const REASONS = [
	["102", "Cancellation of the purchase (e.g. payment voided / reversed)"],
	["103", "Invoice amount wrongly stated"],
	["104", "Partial or complete waive off"],
	["101", "Return of products due to expiry or damage"],
	["105", "Others"],
];

const STATUS_COLOR = {
	submitted: "warning",
	approved: "success",
	rejected: "error",
	cancelled: "default",
};

/**
 * Credit note status + actions for one posted EFRIS document.
 * `doc` needs { id, fiscal_document_number, credit_note }.
 */
export default function EfrisCreditNoteControl({ landlordId, doc, onChanged }) {
	const { applyCreditNote, refreshCreditNote, cancelCreditNote, isSubmitting } = useEinvoicing();
	const [note, setNote] = useState(doc?.credit_note || null);
	const [open, setOpen] = useState(false);
	const [reasonCode, setReasonCode] = useState("102");
	const [reason, setReason] = useState("");
	const [error, setError] = useState("");

	if (!doc?.id) return null;

	const update = (result) => {
		if (result.success) {
			setNote(result.data.data);
			setError("");
			onChanged?.(result.data.data);
		} else {
			setError(result.message);
		}
		return result;
	};

	const handleApply = async () => {
		const result = update(
			await applyCreditNote(landlordId, doc.id, { reason_code: reasonCode, reason })
		);
		if (result.success) setOpen(false);
	};

	const active = note && ["submitted", "approved"].includes(note.status);

	return (
		<>
			{note && (
				<Tooltip title={note.reason_label || ""}>
					<Chip
						size="small"
						color={STATUS_COLOR[note.status] || "default"}
						label={
							note.status === "approved" && note.credit_note_fdn
								? `Credit note ${note.credit_note_fdn}`
								: `Credit note: ${note.status_label || note.status}`
						}
					/>
				</Tooltip>
			)}
			{note?.status === "submitted" && (
				<>
					<Button size="small" disabled={isSubmitting} onClick={async () => update(await refreshCreditNote(note.id))}>
						Check status
					</Button>
					<Button
						size="small"
						color="error"
						disabled={isSubmitting}
						onClick={async () => {
							// eslint-disable-next-line no-alert
							if (window.confirm("Cancel this credit note application at URA?")) {
								update(await cancelCreditNote(note.id));
							}
						}}
					>
						Cancel credit note
					</Button>
				</>
			)}
			{!active && (
				<Button size="small" color="warning" onClick={() => setOpen(true)}>
					Credit note
				</Button>
			)}
			{error && !open && (
				<Typography variant="caption" color="error" sx={{ display: "block" }}>
					{error}
				</Typography>
			)}

			<Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>Credit note for EFRIS {doc.fiscal_document_number}</DialogTitle>
				<DialogContent dividers>
					<Stack spacing={2}>
						<Typography variant="body2">
							Reverses the full EFRIS document at URA. URA reviews and approves the credit note;
							its status updates here.
						</Typography>
						<TextField
							select
							size="small"
							label="Reason"
							value={reasonCode}
							onChange={(e) => setReasonCode(e.target.value)}
						>
							{REASONS.map(([code, label]) => (
								<MenuItem key={code} value={code}>
									{label}
								</MenuItem>
							))}
						</TextField>
						<TextField
							size="small"
							label={reasonCode === "105" ? "Describe the reason (required)" : "Note (optional)"}
							value={reason}
							onChange={(e) => setReason(e.target.value)}
							multiline
							minRows={2}
						/>
						{error && <Alert severity="error">{error}</Alert>}
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setOpen(false)}>Close</Button>
					<Button
						variant="contained"
						color="warning"
						disabled={isSubmitting || (reasonCode === "105" && !reason.trim())}
						onClick={handleApply}
					>
						{isSubmitting ? "Submitting…" : "Submit credit note"}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
